// @flow

import React from 'react'
import { Link } from 'gatsby'
import styled from 'styled-components'
import Layout from 'src/components/layout'
import SEO from 'src/components/seo'
import TaglineSection from 'src/components/tagline-section'
import theme from 'src/theme'
import { rhythm, scale } from 'src/typography'

const PrivacySection = styled.section`
  background: ${props => props.theme.color.contrast.main};
  padding-top: ${rhythm(4)};
  padding-bottom: ${rhythm(2)};
`

const ProseContainer = styled.div`
  width: 50%;
  margin: auto;
  color: ${props => props.theme.color.primary.dark};
  ${props => props.theme.media.phone`
    width: 100%;
    padding-left: ${rhythm(2)};
    padding-right: ${rhythm(2)};
  `}

  h3 {
    color: ${props => props.theme.color.secondary.main};
    ${scale(3 / 4)};
    margin-top: ${rhythm(1)};
    margin-bottom: ${rhythm(1 / 2)};
  }

  p,
  li {
    ${scale(1 / 4)};
    font-weight: 300;
  }

  ul {
    margin-bottom: ${rhythm(1)};
  }

  li {
    margin-bottom: ${rhythm(1 / 4)};
  }

  a {
    color: ${props => props.theme.color.primary.main};
  }

  .updated {
    font-weight: 600;
  }
`

const PrivacyPage = () => (
  <Layout>
    <SEO
      title='Privacy Policy'
      description='How savvasnicholas.com and the savvasnicholas-ads command line tool handle data.'
    />
    <PrivacySection>
      <TaglineSection
        colors={{
          backgroundColor: theme.color.contrast.main,
          textColor: theme.color.primary.main,
        }}
        crazyTextRange={2}
        align='left'
        title={<>Privacy.</>}
        subtitle={`How this website and the savvasnicholas-ads tool handle data.`}
      />
      <ProseContainer>
        <p className='updated'>Last updated: 21 August 2026.</p>

        <p>
          This policy covers two things: this website, savvasnicholas.com, and{' '}
          <Link to='/savvasnicholas-ads'>savvasnicholas-ads</Link>, a personal
          command line tool built and run by me, Savvas Nicholas. I am the owner
          of both.
        </p>

        <h3>This website.</h3>
        <p>
          The site is a static site hosted on Netlify. It does not use
          analytics, tracking cookies, advertising cookies, or any other
          tracking, and it does not build a profile of you. Netlify, as the
          host, keeps ordinary server logs for the requests it serves.
        </p>
        <p>
          The site loads a web font stylesheet from Adobe Fonts
          (use.typekit.net), so your browser makes a request to Adobe when a
          page loads. That is the only third party asset on the site.
        </p>
        <p>
          If you submit the contact form, the name, email address and message
          you type are sent to me through Netlify Forms so that I can reply to
          you. They are used for nothing else. They are not added to a mailing
          list, not sold, and not shared.
        </p>

        <h3>The savvasnicholas-ads tool.</h3>
        <p>
          savvasnicholas-ads is a command line script, run only by me, that
          syncs Google Ads campaign definitions kept in a private source
          repository to my own Google Ads manager account through the Google
          Ads API.
        </p>
        <ul>
          <li>
            It is a single user internal tool. There are no third party users,
            no sign ups, and no accounts.
          </li>
          <li>
            It authenticates through Google OAuth as the account owner only,
            which is me. The OAuth tokens it obtains are stored locally on my
            own machine. They are not transmitted to any third party, and not
            to any server operated by this website.
          </li>
          <li>
            It reads and writes Google Ads campaign data belonging solely to my
            own Google Ads account. It does not read Gmail, Drive, Calendar,
            contacts, or any other Google user data.
          </li>
          <li>
            Data accessed through the Google APIs is not sold, not shared, not
            used for advertising to anyone, and not used to train any model.
          </li>
        </ul>

        <h3>Limited use of Google user data.</h3>
        <p>
          The use of information received from Google APIs by savvasnicholas-ads
          adheres to the{' '}
          <a
            href='https://developers.google.com/terms/api-services-user-data-policy'
            target='_blank'
            rel='noopener noreferrer'
          >
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>

        <h3>Keeping and deleting data.</h3>
        <p>
          Contact form submissions stay in my email for as long as I need them
          to answer you, and are deleted after that. The OAuth tokens held by
          savvasnicholas-ads can be revoked at any time from the Google Account
          permissions page, and deleting the local token file removes them from
          my machine.
        </p>

        <h3>Contact.</h3>
        <p>
          For any question about this policy, or to ask what I hold about you
          and have it deleted, email{' '}
          <a href='mailto:savvas@savvasnicholas.com'>savvas@savvasnicholas.com</a>.
        </p>
      </ProseContainer>
    </PrivacySection>
  </Layout>
)

export default PrivacyPage
